import { observer } from "mobx-react-lite";
import Layout from "../../components/layout/Layout";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useStore } from "../../stores/root-store-context";
import { useEffect, useState } from "react";
import {
    Button,
    message,
    Card,
    Space,
    Typography,
    Steps,
    Spin,
    App,
    Modal
} from "antd";
import { set, toJS } from "mobx";
import { ClockCircleOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { Test, TestAnswer, TestAttempt, TestQuestion, UserTestAssignment } from "../../stores/TestsStore";
import TestStepTwo from "../../components/test/TestStepTwo";
import { GoogleGenerativeAI } from "@google/generative-ai";
import useFullscreenRestrictions from "../../helpers/useFullscreenRestrictions";
import { Languages } from "../../enums/Languages";
import { AssignmentStatus } from "../../enums/AssignmentStatus";
import { TypeTestQuestion } from "../../enums/TypeTestQuestion";

const { Title, Text } = Typography;
const { Step } = Steps;

interface TransformedAnswer {
    questionId: number;
    questionName: string;
    answer: string | number | number[] | undefined;
    maxPoints: number;
    options?: string[];
}

const PassageTest = observer(() => {
    const GEMINI_MODEL = `${process.env.REACT_APP_AI_GEMINI_MODEL}`;
    const API_AI_KEY = `${process.env.REACT_APP_AI_GEMINI_API_KEY}`;
    const [testAttempt, setTestAttempt] = useState<TestAttempt>();
    const [existAssignment, setExistAssignment] = useState<UserTestAssignment | null>();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [test, setTest] = useState<Test>();
    const [testQuestions, setTestQuestions] = useState<TestQuestion[]>([]);
    const [groupedQuestions, setGroupedQuestions] = useState<{ [key: number]: TestQuestion[] }>({});
    const { applicationStore, apiStore } = useStore();
    const { testsStore } = useStore();
    const [loading, setLoading] = useState(false);
    const { message } = App.useApp();
    const { id } = useParams();
    const [currentStep, setCurrentStep] = useState(0);
    const [answers, setAnswers] = useState<
        Array<{
            questionId: number;
            answer: string | number | number[];
        }>
    >([]);
    const [correctAnswers, setCorrectAnswers] = useState<number>(0);
    const [percentageResult, setPercentageResult] = useState<number | null>(null);
    const [existMaxPercentage, setExistMaxPercentage] = useState<number | null>(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [maxTotalPoints, setMaxTotalPoints] = useState<number>(0);
    const [userScore, setUserScore] = useState<number>(0);
    const { enableFullscreenRestrictions, disableFullscreenRestrictions } = useFullscreenRestrictions();

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            if (!testsStore.isInitialized) {
                await testsStore.initializeStore();
            }

            let findedTest = await testsStore.getTestById(id ?? "");

            if (findedTest) {
                var generationQuestions = generateQuestions(findedTest.testQuestions, findedTest.questionsToProvideCount);
                const userAssignments = await testsStore.getAllAssignmentByUserId(applicationStore.user.id ?? "");
                const existAssignment = userAssignments.filter(ua => ua.testId == Number(id)).filter(ua => ua.assignedToUserId == Number(applicationStore.user.id)).find(ua => ua.assignmentStatusId != AssignmentStatus.Completed);

                setExistAssignment(existAssignment ?? null);

                if (existAssignment && existAssignment.testAttempts.length > 0) {
                    const maxPercentag = Math.max(...existAssignment.testAttempts.map(t => t.percentage));
                    setExistMaxPercentage(maxPercentag);
                }

                if (existAssignment && existAssignment.language != Languages.ANY) {
                    const selectedLanguage = existAssignment.language;

                    generationQuestions = Object.fromEntries(
                        Object.entries(generationQuestions).map(([questionNumber, questions]) => [
                            questionNumber,
                            questions.filter(q => q.language === selectedLanguage),
                        ])
                    );
                }

                setGroupedQuestions(generationQuestions);
                setTest(findedTest);
            }
            else {
                message.error(t('test_not_found'));
                navigate(`/${applicationStore.company.Tenant}/tests`);
            }

            setLoading(false);
        };

        fetchData();
    }, [testsStore]);

    const groupQuestionsByNumber = (questions: TestQuestion[], isOneLanguage: boolean) => {
        const grouped: { [key: number]: TestQuestion[] } = {};

        questions.forEach((question) => {
            if (!grouped[question.numberOfQuestion]) {
                grouped[question.numberOfQuestion] = [];
            }
            grouped[question.numberOfQuestion].push(question);
        });

        if (isOneLanguage) {
            const firstLanguage = questions[0]?.language;
            for (const key in grouped) {
                grouped[key] = grouped[key].filter(q => q.language == firstLanguage);
            }
        }

        return grouped;
    };

    const generateQuestions = (testQuestions: TestQuestion[], numberOfQuestions: number) => {
        const typeMap: Record<TypeTestQuestion, 'singleChoice' | 'multipleChoice' | 'writeCode' | 'fixCode' | 'openAnswer'> = {
            [TypeTestQuestion.SingleChoice]: 'singleChoice',
            [TypeTestQuestion.MultipleChoice]: 'multipleChoice',
            [TypeTestQuestion.WriteCode]: 'writeCode',
            [TypeTestQuestion.FixCode]: 'fixCode',
            [TypeTestQuestion.OpenAnswer]: 'openAnswer',
        };

        const questionTypeCounts = testQuestions.reduce((acc, question) => {
            const typeKey = typeMap[question.typeTestQuestionId];
            acc[typeKey] = (acc[typeKey] || 0) + 1;
            return acc;
        }, {} as Record<'singleChoice' | 'multipleChoice' | 'writeCode' | 'fixCode' | 'openAnswer', number>);

        const totalQuestions = testQuestions.length;

        const questionTypePercentages = Object.fromEntries(
            Object.entries(questionTypeCounts).map(([type, count]) => [type, (count / totalQuestions) * 100])
        );

        const questionsToSelect = Object.fromEntries(
            Object.entries(questionTypePercentages).map(([type, percentage]) => [
                type,
                Math.round((percentage / 100) * numberOfQuestions),
            ])
        );

        const getRandomGroups = (groups: TestQuestion[][], count: number) => {
            const shuffled = [...groups].sort(() => Math.random() - 0.5);
            return shuffled.slice(0, count);
        };

        const resultGrouped: { [key: number]: TestQuestion[] } = {};

        Object.entries(questionsToSelect).forEach(([type, count]) => {
            const questionsOfType = testQuestions.filter(
                (q) => typeMap[q.typeTestQuestionId] === type
            );

            const groupedByNumber = groupQuestionsByNumber(questionsOfType, false);

            const selectedGroups = getRandomGroups(Object.values(groupedByNumber), count);

            selectedGroups.forEach((group) => {
                const questionNumber = group[0].numberOfQuestion;
                resultGrouped[questionNumber] = group;
            });
        });

        if (Object.keys(resultGrouped).length < numberOfQuestions) {
            const remainingQuestions = testQuestions.filter(
                (q) => !Object.values(resultGrouped).flat().some((selectedQuestion) => selectedQuestion.id === q.id)
            );

            const remainingGrouped = groupQuestionsByNumber(remainingQuestions, false);
            const remainingGroups = Object.values(remainingGrouped);

            let i = 0;
            while (Object.keys(resultGrouped).length < numberOfQuestions && i < remainingGroups.length) {
                const group = remainingGroups[i];
                const questionNumber = group[0].numberOfQuestion;
                if (!resultGrouped[questionNumber]) {
                    resultGrouped[questionNumber] = group;
                }
                i++;
            }
        }

        return resultGrouped;
    };

    const showModal = () => {
        setIsModalVisible(true);
    };

    const handleOk = () => {
        setIsModalVisible(false);
        handleStartTest();
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const handleStartTest = () => {
        let numberAttempt = 1;

        if (existAssignment != null) {
            numberAttempt = (existAssignment?.testAttempts?.at(-1)?.numberAttempt ?? 0) + 1;
        }

        setTestAttempt({
            id: 0,
            startDate: new Date().toISOString(),
            finishDate: '',
            percentage: 0,
            testId: test?.id ?? 0,
            companyUserId: existAssignment?.assignedToUserId ?? Number(applicationStore.user.id),
            testAnswers: [],
            numberAttempt: numberAttempt,
            timeSpentMinutes: 0,
            userTestAssignmentId: existAssignment?.id ?? null,
        });
        enableFullscreenRestrictions();

        setCurrentStep(1);
    };

    const handleAddQuestion = (question: TestQuestion) => {
        setTestQuestions((prev) => [...prev, question]);
    }

    const transformedAnswersToSave = (
        answers: Array<{ questionId: number; answer: string | number | number[] }>,
        correctAnswers: number[]
    ): TestAnswer[] => {
        if (!test || !testQuestions) {
            return [];
        }

        return testQuestions.map((question) => {
            const answer = answers.find((a) => a.questionId == question.id);

            let answerValue: string;
            if (!answer) {
                answerValue = '';
            } else if (typeof answer.answer === 'number') {
                answerValue = answer.answer.toString();
            } else if (Array.isArray(answer.answer)) {
                answerValue = answer.answer.join(',');
            } else {
                answerValue = answer.answer;
            }

            let answerTextName: string = "";

            if (answer) {
                if (question.typeTestQuestionId === TypeTestQuestion.SingleChoice && typeof answer.answer === 'number') {
                    const option = question.testOptions.find(o => o.id === answer.answer);
                    answerTextName = option ? option.name : "";
                } else if (question.typeTestQuestionId === TypeTestQuestion.MultipleChoice && Array.isArray(answer.answer)) {
                    const selectedOptions = question.testOptions.filter(o => (answer.answer as number[]).includes(o.id));
                    answerTextName = selectedOptions.map(o => o.name).join(', ');
                } else if (typeof answer.answer === 'string') {
                    answerTextName = answer.answer;
                }
            }

            return {
                answer: answerValue,
                answerTextName: answerTextName,
                numberPoints: question.numberPoints,
                questionName: question.name,
                isCorrect: correctAnswers.includes(question.id),
                testQuestionId: question.id,
                testAttemptId: 0,
                id: 0,
            };
        });
    };

    const transformedAnswersToJSONString = (answers: Array<{ questionId: number, answer: string | number | number[] }>) => {
        const transformedAnswers = testQuestions?.map((question) => {
            const answer = answers.find((a) => a.questionId === question.id);

            let transformedAnswer;
            if (!answer) {
                transformedAnswer = "";
            } else {
                switch (question.typeTestQuestionId) {
                    case TypeTestQuestion.SingleChoice:
                        transformedAnswer = question.testOptions.find((o) => o.id === answer.answer)?.name || "";
                        break;
                    case TypeTestQuestion.MultipleChoice:
                        if (Array.isArray(answer.answer)) {
                            transformedAnswer = answer.answer
                                .map((id) => question.testOptions.find((o) => o.id === id)?.name)
                                .filter(Boolean)
                                .join(", ");
                        }
                        break;
                    case TypeTestQuestion.WriteCode:
                    case TypeTestQuestion.FixCode:
                    case TypeTestQuestion.OpenAnswer:
                        transformedAnswer = answer.answer;
                        break;
                    default:
                        transformedAnswer = "Неизвестный тип ответа";
                }
            }

            const maxPoints = question.numberPoints ?? 5;

            const tansformedAnswer: TransformedAnswer = {
                questionId: question.id,
                questionName: question.name,
                answer: transformedAnswer,
                maxPoints
            };

            if (question.testOptions.length > 0 && (question.typeTestQuestionId == TypeTestQuestion.SingleChoice || question.typeTestQuestionId == TypeTestQuestion.MultipleChoice)) {
                tansformedAnswer.options = question.testOptions.map((o) => o.name);
            }

            return tansformedAnswer;
        }).filter(Boolean);

        const jsonString = JSON.stringify(transformedAnswers, null, 2);

        return jsonString;
    }

    const fetchCheckTestsAnswers = async (
        answers: Array<{ questionId: number, answer: string | number | number[] }>,
        minutesSpent: number,
        maxPoints: number
    ) => {

        setLoading(true);

        const jsonString = transformedAnswersToJSONString(answers);

        const requestAI = `
            Please check the following test answers and calculate the score based on the provided maximum points for each question.
            Each question has a maximum number of points, and the user's score for each question should be calculated accordingly.
            The scoring system is as follows:
            - If the percentage is greater than or equal to 90%, the grade is 5.
            - If the percentage is between 75% and 89%, the grade is 4.
            - If the percentage is between 60% and 74%, the grade is 3.
            - If the percentage is below 60%, the grade is 2.

            For each question:
            - If the answer is correct, award the maximum points for that question.
            - If the answer is incorrect, award 0 points for that question.
            - If the options are null or empty, do not consider them in the evaluation.
            - If the options are not null and not empty, evaluate the answers based on the provided options.

            Additionally, return an array of "questionId" for all correctly answered questions.

            Here are the user's answers and the maximum points for each question in JSON format:
            ${jsonString}

            Calculate the percentage using the formula:
            (userScore / ${maxPoints}) * 100

            Format the output as a JSON object with the following fields:
            - "grade": The calculated grade.
            - "correctCount": The total number of correctly answered questions.
            - "totalQuestions": The total number of questions in the test.
            - "maxScore": The maximum possible score (${maxPoints} points).
            - "userScore": The total score achieved by the user based on their answers.
            - "percentage": The percentage of the maximum score achieved.
            - "correctAnswers": An array of "questionId" for all correctly answered questions.

            Return the JSON object with these fields as output. Do not include any additional text, comments, or explanations.
        `;

        try {
            const genAI = new GoogleGenerativeAI(API_AI_KEY);
            const model = genAI.getGenerativeModel({ model: GEMINI_MODEL });

            const resultResponse = await model.generateContent(requestAI);

            const rawResponse = resultResponse.response.text().trim();
            const jsonStart = rawResponse.indexOf("{");
            const jsonEnd = rawResponse.lastIndexOf("}") + 1;

            if (jsonStart === -1 || jsonEnd === 0) {
                throw new Error("Response does not contain a valid JSON object.");
            }

            const cleanedResponse = rawResponse.substring(jsonStart, jsonEnd);

            const result = JSON.parse(cleanedResponse);

            const { grade, correctCount, totalQuestions, percentage, maxScore, userScore, correctAnswers } = result;

            setCorrectAnswers(correctCount);
            setUserScore(userScore);
            await fetchGradeToServer(correctAnswers, minutesSpent, maxPoints, answers);
        } catch (error) {
            console.error("Error fetching data from API:", error);
        }
        finally {
            setLoading(false);
        }
    };

    const fetchGradeToServer = async (correctAnswers: number[], minutesSpent: number, maxPoints: number, answers: Array<{ questionId: number, answer: string | number | number[] }>) => {
        let testAttemptData: TestAttempt | null = testAttempt ?? null;

        let obtainedScore = 0;
        testQuestions?.forEach(testQuestion => {
            if (correctAnswers.some(correctId => correctId == testQuestion.id)) {
                obtainedScore += testQuestion.numberPoints;
            }
        });

        const percentage = Math.round(maxPoints > 0 ? (obtainedScore / maxPoints) * 100 : 0);
        setPercentageResult(Math.round(Number(percentage)));

        if (testAttemptData != null) {
            testAttemptData.percentage = Math.round(Number(percentage));
            testAttemptData.timeSpentMinutes = minutesSpent;
            testAttemptData.finishDate = new Date().toISOString();
            testAttemptData.testAnswers = transformedAnswersToSave(answers, correctAnswers);

            try {
                await testsStore.addTestAttempt(testAttemptData);

            } catch (error) {
                message.error(t("Failed"));
            }
        }
    }

    const handleGetLanguage = () => {
        if (test) {
            if (existAssignment != null) {
                return existAssignment.language == "all" ? t("languages.any") : existAssignment.language == "en" ? t("add_test.English") : existAssignment.language == "ua" ? t("add_test.Ukrainian") : t("add_test.Russian");
            }
            else {
                return test.language == "all" ? t("languages.any") : test.language == "en" ? t("add_test.English") : test.language == "ua" ? t("add_test.Ukrainian") : t("add_test.Russian");
            }
        }
    }

    const onFinishTest = async (answers: Array<{ questionId: number, answer: string | number | number[] }>, minutesSpent: number) => {
        setAnswers(answers);
        disableFullscreenRestrictions();

        const maxPoints = testQuestions.reduce((total, question) => total + (question.numberPoints ?? 0), 0) ?? 0;
        setMaxTotalPoints(maxPoints);

        await fetchCheckTestsAnswers(answers, minutesSpent, maxPoints);
        setCurrentStep(2);
    }

    const getAttemptUsers = (): string | undefined => {
        if (existAssignment != null) {
            const numberOfAttempts = existAssignment.numberOfAttempts ?? 0;
            const attemptsLength = existAssignment?.testAttempts.length ?? 0;
            return `${t("passage_test.attemptsRemaining")}: ${numberOfAttempts - attemptsLength}`;
        }

        return `${t("passage_test.number_of_attempts")}: ${test?.numberOfAttempts ?? 0}`;
    };

    return (
        <Layout headerStyle={1} footerStyle={2}>
            <Spin spinning={loading} style={{ minHeight: 600 }}>
                <div style={{ padding: "20px" }}>
                    <Steps current={currentStep} style={{ marginBottom: "20px" }}>
                        <Step title={t(`passage_test.test_details`)} />
                        <Step title={t("passage_test.test_in_progress")} />
                        <Step title={t("passage_test.test_complete")} />
                    </Steps>

                    {currentStep === 0 && (
                        <Card style={{ maxWidth: 400, margin: "0 auto", textAlign: "center" }}>
                            <Space direction="vertical" size="large" style={{ width: "100%" }}>
                                <Title level={3}>{t("passage_test.test_details")}</Title>
                                <Text>{t("passage_test.topic")}: {applicationStore.skills.find(s => s.skillId == test?.skillId?.toString())?.title || t("unknown_skill")}</Text>
                                <Text>{t("add_test.Level")}: {test?.level}</Text>
                                <Text>{getAttemptUsers()}</Text>
                                {existMaxPercentage != null && (
                                    <Text>{t(`passage_test.max_grade`)}: {existMaxPercentage}%</Text>
                                )}
                                {test?.language != null && applicationStore.siteConfig.isShowLanguage && (
                                    <Text>{t("tests.Language")}: {handleGetLanguage()}</Text>
                                )}
                                <Text>
                                    <QuestionCircleOutlined /> {t("passage_test.number_of_questions")}: {test?.questionsToProvideCount}
                                </Text>
                                <Text>
                                    <ClockCircleOutlined /> {t("passage_test.time_limit")}: {test?.maxTimeMinutes} {t("passage_test.minutes")}
                                </Text>
                                {(test?.description != null && test.description.length > 0) && (
                                    <Text>{t("add_test.Description")}: {test?.description}</Text>
                                )}
                                <Button
                                    type="primary"
                                    size="large"
                                    block
                                    onClick={showModal}
                                >
                                    {t("passage_test.start_test")}
                                </Button>
                            </Space>
                        </Card>
                    )}

                    {currentStep === 1 && (
                        <TestStepTwo
                            language={existAssignment ? existAssignment.language ?? "any" : Languages.ANY}
                            maxTimeMinutes={test?.maxTimeMinutes ?? 15}
                            testGroupQuestions={groupedQuestions}
                            currentTestAttempt={testAttempt!}
                            addNewQuestion={handleAddQuestion}
                            onFinish={onFinishTest}
                        />
                    )}

                    {currentStep === 2 && (
                        <Card style={{ maxWidth: 400, margin: "0 auto", textAlign: "center" }}>
                            <Title level={3}>{t("passage_test.test_complete")}</Title>
                            <div style={{ marginTop: "30px" }}>
                                <Title level={4}>{t("passage_test.your_score")}:</Title>
                                <Text>{t("passage_test.correct_answers")}: {correctAnswers} / {test?.questionsToProvideCount}</Text>
                                <Text style={{ display: "block", marginTop: "10px" }}>
                                    {t("passage_test.score")}: {percentageResult}%
                                </Text>
                                <Text style={{ display: "block", marginTop: "10px" }}>
                                    {t("passage_test.score_balls")}: {userScore} / {maxTotalPoints}
                                </Text>
                                <Text style={{ display: "block", marginTop: "10px", fontSize: "17px", fontWeight: "bold" }}>
                                    {(percentageResult ?? 0) > 80
                                        ? t("passage_test.test_passed")
                                        : t("passage_test.test_failed")}
                                </Text>
                            </div>
                            <Button style={{ marginTop: 20 }} type="primary" onClick={() => navigate(`/${applicationStore.company.Tenant}/tests`)}>
                                {t("passage_test.back_to_tests")}
                            </Button>
                        </Card>
                    )}
                </div>

                <Modal
                    title={<span style={{ color: 'black', fontWeight: 'bold', fontSize: '18px' }}>{t("passage_test.modal_title")}</span>}
                    open={isModalVisible}
                    onOk={handleOk}
                    onCancel={handleCancel}
                    okText={t("passage_test.modal_confirm")}
                    cancelText={t("passage_test.modal_cancel")}
                >
                    <p style={{ color: 'red', fontWeight: 'bold', fontSize: '16px' }}>
                        {t("passage_test.modal_content")}
                    </p>
                </Modal>
            </Spin>
        </Layout>
    );
});

export default PassageTest;
